/**
 * Shared utilities for parsing inline Ask references used in markdown:
 * - [hexVis:<cellId>]
 * - [hexProject:<projectId>]
 * - [hexCell:<cellId>] (legacy tag for cell references)
 * - [hexTask:<taskId>]
 * - [hexFile:<fileId>] (downloadable project file references)
 * - C01, C10, etc. (bare cell references)
 *
 * Keep this in sync with the agent prompts.
 *
 * Adding a new reference tag type? Make sure to:
 * 1. Declare a constant here and extend the regex below.
 * 2. Teach the remark plugin in remarkHexInlineReferences.ts how to handle the tag.
 * 3. Allow the tag's attributes in MarkdownSanitizeSchema.ts.
 * 4. Render the tag inside InlineMarkdownRenderer.tsx.
 * 5. Update downstream renderers (sharedMessageFormatting.ts, agentSlackbotUtils.ts, MCP formatters, etc.) so secondary channels understand the new tag.
 * 6. Cover the parser + renderer behavior with tests since these paths are too easy to break.
 */

import { HexId, StaticCellId } from "../idTypeBrands.js";

export const HEX_VIS_TAG = "hexVis";
export const HEX_PROJECT_TAG = "hexProject";
export const HEX_CELL_TAG = "hexCell";
export const HEX_TASK_TAG = "hexTask";
export const HEX_FILE_TAG = "hexFile";
export const UNRESOLVED_PROJECT_REFERENCE_ID = "unresolved";

// Separator used for composite ids that include an "external" app/project prefix.
// Example: `<staticCellId>@<hexId>`
export const HEX_EXTERNAL_CELL_REFERENCE_SEPARATOR = "@";

/**
 * Parse a display-form cell reference ID that may be an external (cross-project)
 * reference in the format `<staticCellId>@<hexId>`.
 */
export function parseExternalCellReferenceId(
  referenceId: string,
): { staticCellId: StaticCellId; hexId: HexId } | null {
  const separatorIndex = referenceId.indexOf(
    HEX_EXTERNAL_CELL_REFERENCE_SEPARATOR,
  );
  if (separatorIndex <= 0 || separatorIndex === referenceId.length - 1) {
    return null;
  }
  return {
    staticCellId: referenceId.slice(0, separatorIndex) as StaticCellId,
    hexId: referenceId.slice(separatorIndex + 1) as HexId,
  };
}

export const getReferenceRegex = (): RegExp =>
  new RegExp(
    // Optional backtick before and after the bracketed reference (e.g., `[hexCell:C01]`)
    `\`?\\[(${HEX_VIS_TAG}|${HEX_PROJECT_TAG}|${HEX_CELL_TAG}|${HEX_TASK_TAG}|${HEX_FILE_TAG}):([a-zA-Z0-9_@-]{1,128})(?::([^\\]]+))?\\]\`?`,
    "g",
  );

// Matches bare cell references like C01, C10, C1000, etc.
// Also supports cross-project refs like P01C01, P2C10, P12C1000, etc.
// Supports optional backtick wrapping (e.g., `C01`)
// Uses lookbehind/lookahead to ensure word boundaries or backticks
const bareCellReferenceRegex =
  /(?<=^|[^a-zA-Z0-9])`?(?:C\d{1,4}|P\d{1,4}C\d{1,4})`?(?=$|[^a-zA-Z0-9])/g;

export type InlineReferenceTextChunk = {
  type: "MARKDOWN";
  content: string;
};

export type InlineReferenceCellChunk = {
  type: "CELL_REFERENCE";
  /** This may be a staticCellId or a cellId (pre-January 2026) */
  cellId: string;
  /**
   * Optional, decoded label segment from `[hexCell:<id>:<encodedLabel>]`.
   *
   * Note: This is only available for tagged references, not for bare references like `C01`.
   * Consumers should treat it as a best-effort display hint (e.g. for missing cells).
   */
  label?: string;
  referenceTag: typeof HEX_VIS_TAG | typeof HEX_CELL_TAG;
  /**
   * True when this reference was parsed from a bare short ref (e.g. `C01`)
   * rather than an explicit tagged ref like `[hexCell:C01]`.
   * `originalText` preserves the matched text including any backtick wrapping.
   */
  bare?: boolean;
  originalText?: string;
};

export type InlineReferenceProjectChunk = {
  type: "PROJECT_REFERENCE";
  projectRef: string;
  bare?: boolean;
};

export type InlineReferenceTaskChunk = {
  type: "TASK_REFERENCE";
  taskId: string;
};

export type InlineReferenceFileChunk = {
  type: "FILE_REFERENCE";
  /** The id of the project file to make downloadable. */
  fileId: string;
};

export type InlineReferenceChunk =
  | InlineReferenceTextChunk
  | InlineReferenceCellChunk
  | InlineReferenceProjectChunk
  | InlineReferenceTaskChunk
  | InlineReferenceFileChunk;

/**
 * Splits markdown into chunks of plain text and references.
 * Handles both [hexCell:C01] (tagged refs) and C01 (bare refs).
 */
export function parseInlineReferences(
  markdown: string,
): InlineReferenceChunk[] {
  const chunks: InlineReferenceChunk[] = [];

  const referenceRegex = getReferenceRegex();
  referenceRegex.lastIndex = 0;

  let lastIndex = 0;
  let match: RegExpExecArray | null;
  // Add text before the match, if any
  while ((match = referenceRegex.exec(markdown)) != null) {
    if (match.index > lastIndex) {
      const textContent = markdown.slice(lastIndex, match.index);
      chunks.push({ type: "MARKDOWN", content: textContent });
    }

    const patternType = match[1];
    const id = match[2];
    const rawLabel = match[3];
    if (!patternType || !id) {
      continue;
    }

    if (patternType === HEX_VIS_TAG || patternType === HEX_CELL_TAG) {
      const referenceTag =
        patternType === HEX_VIS_TAG ? HEX_VIS_TAG : HEX_CELL_TAG;
      const label = decodeInlineReferenceLabel(rawLabel);
      chunks.push({
        type: "CELL_REFERENCE",
        cellId: id,
        ...(label != null ? { label } : {}),
        referenceTag,
      });
    } else if (patternType === HEX_PROJECT_TAG) {
      chunks.push({
        type: "PROJECT_REFERENCE",
        projectRef: id,
      });
    } else if (patternType === HEX_TASK_TAG) {
      chunks.push({
        type: "TASK_REFERENCE",
        taskId: id,
      });
    } else if (patternType === HEX_FILE_TAG) {
      chunks.push({
        type: "FILE_REFERENCE",
        fileId: id,
      });
    }

    lastIndex = match.index + match[0].length;
  }

  // Add remaining text after the last match
  if (lastIndex < markdown.length) {
    chunks.push({ type: "MARKDOWN", content: markdown.slice(lastIndex) });
  }

  // If nothing matched, return the full markdown as a single text chunk
  if (chunks.length === 0) {
    chunks.push({ type: "MARKDOWN", content: markdown });
  }

  return parseBareProjectReferences(parseBareCellReferences(chunks));
}

/**
 * Scans MARKDOWN chunks for bare references matching `regex`, replacing each
 * match with a chunk returned by `toChunk`. MARKDOWN chunks without matches
 * pass through unchanged; non-MARKDOWN chunks are never touched.
 *
 * `toChunk` may return `null` to skip a match (e.g. percentile filtering).
 */
const parseBareReferences = (
  chunks: InlineReferenceChunk[],
  regex: RegExp,
  toChunk: (match: RegExpExecArray) => InlineReferenceChunk | null,
): InlineReferenceChunk[] => {
  const result: InlineReferenceChunk[] = [];

  for (const chunk of chunks) {
    if (chunk.type !== "MARKDOWN") {
      result.push(chunk);
      continue;
    }

    const text = chunk.content;
    regex.lastIndex = 0;

    let lastIndex = 0;
    let match: RegExpExecArray | null;

    while ((match = regex.exec(text)) != null) {
      const parsed = toChunk(match);
      if (parsed == null) {
        continue;
      }

      if (match.index > lastIndex) {
        result.push({
          type: "MARKDOWN",
          content: text.slice(lastIndex, match.index),
        });
      }

      result.push(parsed);
      lastIndex = match.index + match[0].length;
    }

    if (lastIndex === 0) {
      result.push(chunk);
    } else if (lastIndex < text.length) {
      result.push({ type: "MARKDOWN", content: text.slice(lastIndex) });
    }
  }

  return result;
};

const parseBareCellReferences = (
  chunks: InlineReferenceChunk[],
): InlineReferenceChunk[] =>
  parseBareReferences(chunks, bareCellReferenceRegex, (match) => {
    const originalText = match[0];
    return {
      type: "CELL_REFERENCE",
      cellId: originalText.replace(/`/g, ""),
      referenceTag: HEX_CELL_TAG,
      bare: true,
      originalText,
    };
  });

// Matches bare project references like P01, P10, P1000, etc.
// Does NOT match cross-project cell refs (P01C01) since those are already
// handled as CELL_REFERENCE by parseBareCellReferences.
// Uses lookbehind/lookahead for word boundaries, supports backtick wrapping.
const bareProjectReferenceRegex =
  /(?<=^|[^a-zA-Z0-9])`?P(\d{1,4})`?(?!C\d)(?=$|[^a-zA-Z0-9])/g;

const parseBareProjectReferences = (
  chunks: InlineReferenceChunk[],
): InlineReferenceChunk[] =>
  parseBareReferences(chunks, bareProjectReferenceRegex, (match) => {
    return {
      type: "PROJECT_REFERENCE",
      projectRef: match[0].replace(/`/g, ""),
      bare: true,
    };
  });

export function encodeInlineReferenceLabel(label: string): string {
  return encodeURIComponent(label);
}

export function decodeInlineReferenceLabel(
  encodedLabel: string | undefined,
): string | undefined {
  if (!encodedLabel) {
    return undefined;
  }
  try {
    return decodeURIComponent(encodedLabel);
  } catch {
    return encodedLabel;
  }
}
